import { globalStyles } from './globals';

export const fonts = [
  'https://fonts.googleapis.com/css2?family=Noto+Sans:ital,wght@0,100..900;1,100..900&display=swap',
];

/**
 * This constant was created to have a single source of truth within this file
 * for the primary, secondary, and tertiary colors for each branding.
 * These three colors are the most important for each branding.
 * These colors are defined in the brandbook of each partner.
 */
const BASE_COLORS = {
  brandPrimary: '#005253',
  brandSecondary: '#924DC2',
  brandTertiary: '#8F1C5F',
} as const;

export const styles = {
  ...globalStyles,

  // Brand Color
  colorBrandPrimary: '#005253',
  colorBrandSecondary900: '#00393A',
  colorBrandSecondary400: '#71B2B6',
  colorBrandSecondary100: '#B4E1E7',

  // ---- Buttons ----

  // Primary
  colorButtonPrimaryDefault: '#005253',
  colorButtonPrimaryHover: '#00393A',

  // Secondary
  colorButtonSecondaryDefault: '#005253',
  colorButtonSecondaryHover: '#00393A',

  // Tertiary
  colorButtonTertiaryDefault: '#E5E7EB',
  colorButtonTertiaryHover: '#F3F4F6',
  colorButtonTertiaryBorder: '#E5E7EB',

  // Danger
  colorButtonDangerDefault: '#C81E1E',
  colorButtonDangerHover: '#9B1C1C',

  // Ghost
  colorButtonGhostDefault: 'transparent',
  colorButtonGhostHover: '#fbf9fa',

  // ---- Inputs ----
  colorInputBorderDefault: '#d1d5dc', // gray-300
  colorInputBorderFocused: '#005253',
  colorInputBorderError: '#C10007', // Tailwind red-700 (was red-900)
  colorInputBorderDisabled: '#F7F7F7',
  colorInputBackgroundDisabled: '#F7F7F7',

  colorInputDropdownTextDefault: '#677b88',

  colorInputTogglePrimary: '#14af6e',
  colorInputToggleSecondary: '#005253',

  // ----- Charts -----

  // Billing
  colorGraphCategory1: '#005253',
  colorGraphCategory2: '#005F94',
  colorGraphCategory3: '#154A92',
  colorGraphCategory4: '#7DC0A7',
  colorGraphCategory5: '#8689DD',
  colorGraphCategory6: '#005F94',
  colorGraphCategory7: BASE_COLORS.brandPrimary,
  colorGraphCategory8: '#005F94',
  colorGraphCategory9: '#005253',
  colorGraphCategory10: '#154A92',

  colorGraphFailure1: '#C22222',
  colorGraphFailure2: '#D63535',
  colorGraphFailure3: '#E05656',
  colorGraphFailure4: '#E66969',
  colorGraphFailure5: '#EB9090',

  colorGraphExpired1: '#E0529A',
  colorGraphExpired2: '#FF69B4',
  colorGraphExpired3: '#FF8CC5',
  colorGraphExpired4: '#FFA3CF',
  colorGraphExpired5: '#FFB9DA',

  colorGraphSuperseded1: '#E57A32',
  colorGraphSuperseded2: '#FF914D',
  colorGraphSuperseded3: '#FFB07A',
  colorGraphSuperseded4: '#FFCBA6',
  colorGraphSuperseded5: '#FFE2C7',

  colorGraphIneligible1: '#6B7280',
  colorGraphIneligible2: '#9CA3AF',
  colorGraphIneligible3: '#D1D5DB',

  colorGraphBilling1: '#005253',
  colorGraphBilling2: '#154A92',

  colorGraphCategoryError1: '#A83232',
  colorGraphCategoryError2: '#D9534F',

  // --- On Boarding ----

  // Journey
  colorOnboardingJourneyBg: '#71B2B6',
  colorOnboardingJourneyText: '#000000',
  colorOnBoardingJourneyNumberBg: '#B4E1E7',

  // Prelude
  colorOnBoardingPreludeBg: '#FFFFFF',

  // Backgrounds
  colorBackgroundPrimary: '#F7F7F7',
  colorBackgroundHighlight: '#094371',
  colorBackgroundDashboardLinks: '#B4E1E7',

  // Fonts
  // Tailwind gray-900 — headings/primary text (design direction 2026-07)
  colorFontPrimary: '#101828',
  colorFontSecondary: '#000000',

  // typography
  fontLight: "'Noto Sans', sans-serif",
  fontStack: "'Noto Sans', sans-serif",
  fontBold: "'Noto Sans', sans-serif",
  fontBook: "'Noto Sans', sans-serif",
  fontOsRegular: "'Noto Sans', sans-serif",
  fontOsSemibold:
    "'OpenSans-SemiBold', 'Roboto', Tahoma, Helvetica, Arial, sans-serif",
  fontReceipt:
    "'Roboto-Regular', 'OpenSans-Regular', Tahoma, Helvetica, Arial, sans-serif",
  fontReceiptBold:
    "'Roboto-Bold', 'OpenSans-SemiBold', Tahoma, Helvetica, Arial, sans-serif",
  fontLanguageBold:
    "'SegoeUI-Bold', 'OpenSans-SemiBold', Tahoma, Helvetica, Arial, sans-serif",

  // Components

  // TestTable
  colorTestTableActiveBgItem: '#B4E1E7',
  colorTestTableActiveBorderItem: '#005253',
  colorTestTableActiveTextItem: '#000000',

  // Accordion
  colorAccordionActiveBgItem: '#e6eeee',

  // Side Menu
  colorSideMenuActiveBgItem: '#F7F7F7',

  // Language Selector
  colorBgLanguageSelectorChip: '#B4E1E7',
  colorBgLanguageSelectorSelectedOption: '#f5f8fe',

  // Select
  colorBgSelectSelected: '#B4E1E7',

  // WikiBox
  colorWikiBoxMenuBg: '#B4E1E7',
  colorWikiBoxMenuTitle: '#3a474e',
  colorWikiBoxMenuText: '#3a474e',

  // Developers Tables
  colorDocTableBg: '#B4E1E7',
  colorDocTableCellBg: '#B4E1E7',

  // Developers Tables
  colorDocTableCellTitle: '#3a474e',

  // Portal UI Colors
  colorSecondary: BASE_COLORS.brandSecondary,
  colorTertiary: BASE_COLORS.brandTertiary,
  disabledButtonBg: BASE_COLORS.brandTertiary,

  // New variables
  borderColor: '#082E4D',
  colorPrimaryHover: '#2E1B46',
  // Keyboard focus ring — must keep >= 3:1 contrast on white (WCAG 1.4.11)
  colorFocus: BASE_COLORS.brandPrimary,
  bgPrimary: '#e6eeee',
  bgPrimaryHover: '#e6eeee',
  colorSecondaryHover: '#7f37b1',
  bgSecondary: '#3C0977',
  bgSecondaryHover: '#320763',
  colorTertiaryHover: '#06411F',
  bgTertiary: '#e6eeee',
  bgTertiaryHover: '#A5B1B8',
  uiTexticonLight: '#460A8A',
  uiBgDark: '#00393A',
  uiBgLight: '#fff',
  uiBgHeader: 'rgba(0, 82, 83, 0.1)',
  brand01: '#924DC2',
  brand02: '#035827',
  brand03Blue: BASE_COLORS.brandPrimary,
  developerDoc: '#e8eef9',
  inactiveMenu: '#5e656c',
  blue4: '#B4E1E7',

  // Variables
  indigo500: '#7568d8',
  blueMint: '#c1e4f1',
  grayPorcelain: '#EAECEE',
  loadingColor: BASE_COLORS.brandPrimary,
  ellipsisHighlightColor: '#48042B',
  ellipsisHighlightColorLight: BASE_COLORS.brandPrimary,
  ellipsisColor: '#0A3A61',
  selectedBlue: '#016F2F',
  purple: '#8F1C5F',

  // Auth
  authBg: '#fff',
  backgroundAuthSize: 'cover',
  backgroundAuthPosition: 'right',
  backgroundAuthRepeat: 'no-repeat',

  // Checkboxes Colors
  checkboxDefault: '#005253',
  checkboxPrimary: '#005253',

  // Loader Colors
  apiCardPrimaryBg: '#00393A',
  apiCardPrimaryStart: '#005253',
  apiCardPrimaryStop: '#004445',
  apiCardSecondaryBg: '#00393A',
  apiCardSecondaryStart: '#005253',
  apiCardSecondaryStop: '#004445',
  apiCardTertiaryBg: '#00393A',
  apiCardTertiaryStart: '#005253',
  apiCardTertiaryStop: '#004445',

  qualityServiceLevel1: '#E6EEEE',
  qualityServiceLevel2: '#DFEDED',
  qualityServiceLevel3: '#CB4141',
  qualityServiceTooltipBg: '#fff',
  qualityServiceTooltipColor: '#000',

  // System Colors
  deviceSystemBlack: '#1C1C1E',
};
